// 滑块拼图验证码（图形补全型）
// 后端返回带缺口的背景 SVG 与拼图块 SVG；前端拖动滑块把拼图块放入缺口。
//
// 布局:canvas 默认 280px 宽,但会被父容器约束(LoginPage 等窄屏容器),
// 通过 CSS `width: 100%` + `max-width: 280px` + `aspect-ratio` 实现响应式,
// 避免父容器 < 280px 时把 canvas 撑爆、溢出相邻内容。
import { useEffect, useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { ArrowCounterclockwiseRegular } from '@fluentui/react-icons';
import type { CaptchaChallenge } from '@/api/auth';

const BG_WIDTH = 280;
const BG_HEIGHT = 160;
const PIECE_WIDTH = 50;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    userSelect: 'none',
    // 让父 flex 容器能正确传递宽度(canvas 才会跟着收缩)
    minWidth: 0,
    width: '100%',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
  // canvas 默认 280px;若父容器更窄,通过 max-width + aspect-ratio 自适应缩放。
  // 父容器更宽则保持 280px 不被撑大(让左右列宽度可控)。
  canvas: {
    position: 'relative',
    width: '100%',
    maxWidth: `${BG_WIDTH}px`,
    aspectRatio: `${BG_WIDTH} / ${BG_HEIGHT}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  background: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    '& svg': {
      display: 'block',
      width: '100%',
      height: '100%',
    },
  },
  piece: {
    position: 'absolute',
    left: 0,
    top: 0,
    // 拼图块宽度按 canvas 实际宽度比例缩放(50/280)
    width: `${(PIECE_WIDTH / BG_WIDTH) * 100}%`,
    aspectRatio: '1 / 1',
    transition: 'none',
    filter: `drop-shadow(0 2px 4px ${tokens.colorNeutralShadowAmbient})`,
    '& svg': {
      display: 'block',
      width: '100%',
      height: '100%',
    },
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  slider: {
    flex: 1,
    height: '32px',
    WebkitAppearance: 'none',
    appearance: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    '::-webkit-slider-runnable-track': {
      height: '6px',
      borderRadius: tokens.borderRadiusCircular,
      backgroundColor: tokens.colorNeutralBackground4,
    },
    '::-webkit-slider-thumb': {
      WebkitAppearance: 'none',
      appearance: 'none',
      width: '28px',
      height: '28px',
      marginTop: '-11px',
      borderRadius: tokens.borderRadiusCircular,
      backgroundColor: tokens.colorBrandBackground,
      border: `2px solid ${tokens.colorNeutralBackground1}`,
      boxShadow: tokens.shadow4,
    },
    '::-moz-range-track': {
      height: '6px',
      borderRadius: tokens.borderRadiusCircular,
      backgroundColor: tokens.colorNeutralBackground4,
    },
    '::-moz-range-thumb': {
      width: '28px',
      height: '28px',
      borderRadius: tokens.borderRadiusCircular,
      backgroundColor: tokens.colorBrandBackground,
      border: `2px solid ${tokens.colorNeutralBackground1}`,
      boxShadow: tokens.shadow4,
    },
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: `${BG_WIDTH}px`,
    aspectRatio: `${BG_WIDTH} / ${BG_HEIGHT}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
  },
});

export interface SliderCaptchaProps {
  challenge: CaptchaChallenge | null;
  onChange: (x: number) => void;
  onRefresh: () => void;
  loading?: boolean;
}

export function SliderCaptcha({
  challenge,
  onChange,
  onRefresh,
  loading,
}: SliderCaptchaProps) {
  const styles = useStyles();
  const [x, setX] = useState(0);

  // 每次拿到新挑战都重置滑块位置
  useEffect(() => {
    setX(0);
    onChange(0);
  }, [challenge?.token, onChange]);

  // 滑块位移 x 是基于 280 基准的像素坐标(直接发往后端验证)。
  // canvas 实际宽度不一定是 280(响应式缩放),所以:
  // - slider 的 `max` 跟着 canvas 实际可移动范围走,保证 thumb 走到尽头 = piece 走到 canvas 尽头
  // - piece 的视觉位移 = x * scale,把"基于 280 的像素"换算成"基于实际 canvas 的像素"
  // - 这样:用户拖 thumb 走完 track 比例 t 时,piece 视觉位移正好 = t * (canvas 可移动范围)
  const [scale, setScale] = useState(1); // canvas 实际宽 / 280
  const [canvasWidth, setCanvasWidth] = useState(BG_WIDTH);
  const [_canvasEl, setCanvasEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!challenge) return;
    const el = document.querySelector('[data-captcha-canvas="1"]') as HTMLDivElement | null;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        setCanvasWidth(w);
        setScale(w > 0 ? w / BG_WIDTH : 1);
      }
    });
    ro.observe(el);
    setCanvasWidth(el.clientWidth);
    setScale(el.clientWidth > 0 ? el.clientWidth / BG_WIDTH : 1);
    return () => ro.disconnect();
  }, [challenge]);

  const pieceTop = challenge ? challenge.y : 0;

  const backgroundSvg = useMemo(() => {
    if (!challenge) return null;
    // 给 SVG 注入唯一 id,避免同一页多个 mask/clipPath id 冲突
    const uid = challenge.token.slice(0, 8);
    return challenge.background_svg
      .replace(/id="hole"/g, `id="hole-${uid}"`)
      .replace(/url\(#hole\)/g, `url(#hole-${uid})`);
  }, [challenge]);

  const pieceSvg = useMemo(() => {
    if (!challenge) return null;
    const uid = challenge.token.slice(0, 8);
    return challenge.piece_svg
      .replace(/id="piece"/g, `id="piece-${uid}"`)
      .replace(/url\(#piece\)/g, `url(#piece-${uid})`);
  }, [challenge]);

  // slider 的 max 用 canvas 实际可移动范围(像素),piece 也用 canvas 像素平移,
// 然后发往后端的 x 把实际像素除以 scale 换算回 280 基准。
  const maxXPx = challenge
    ? canvasWidth - (PIECE_WIDTH / BG_WIDTH) * canvasWidth
    : canvasWidth;

  function handleSliderChange(e: React.ChangeEvent<HTMLInputElement>) {
    const pxValue = Number(e.target.value);
    setX(pxValue);
    // 把基于 canvas 的实际像素换算回 280 基准,作为 captcha_x 发往后端验证。
    onChange(scale > 0 ? Math.round(pxValue / scale) : pxValue);
  }

  if (!challenge) {
    return (
      <div className={styles.root}>
        <div className={styles.labelRow}>
          <Button
            appearance="transparent"
            size="small"
            icon={<ArrowCounterclockwiseRegular />}
            onClick={onRefresh}
            disabled={loading}
          >
            刷新
          </Button>
        </div>
        <div className={styles.placeholder}>
          {loading ? <Spinner size="tiny" /> : '点击刷新加载验证码'}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.labelRow}>
        <Button
          appearance="transparent"
          size="small"
          icon={<ArrowCounterclockwiseRegular />}
          onClick={onRefresh}
          disabled={loading}
        >
          刷新
        </Button>
      </div>

      <div className={styles.canvas} data-captcha-canvas="1" aria-label="滑块拼图验证码" ref={setCanvasEl}>
        <div
          className={styles.background}
          dangerouslySetInnerHTML={{ __html: backgroundSvg ?? '' }}
        />
        <div
          className={styles.piece}
          style={{
            // x 是 280 基准的像素值,乘以 scale 才是真实位移
            transform: `translate(${x * scale}px, ${pieceTop * scale}px)`,
          }}
          dangerouslySetInnerHTML={{ __html: pieceSvg ?? '' }}
        />
      </div>

      <div className={styles.controls}>
        <input
          type="range"
          min={0}
          max={maxXPx}
          value={x}
          onChange={handleSliderChange}
          disabled={loading}
          className={styles.slider}
          aria-label="拖动滑块完成拼图"
        />
      </div>
    </div>
  );
}